import { type GetStaticProps } from 'next';
import { BRAND_NAME, ORGANIZATION_ID, SITE_URL } from 'constants/products';
import { TENNIS_RATING_CONVERTER_PAGE } from 'constants/pages';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generateArticleJsonLd } from 'utils/shared/json-ld/article';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { generateFaqJsonLd } from 'utils/shared/json-ld/faq';
import { generateSoftwareApplicationJsonLd } from 'utils/shared/json-ld/softwareApplication';
import {
  PRIMARY_TENNIS_SYSTEM_IDS,
  TENNIS_SYSTEMS,
} from 'utils/shared/tennisRatingConverter';
import TennisRatingConverter, {
  FAQS,
  type PageProps,
} from 'screens/TennisRatingConverter';

const PAGE_URL = `${SITE_URL}${TENNIS_RATING_CONVERTER_PAGE}`;
const PAGE_TITLE = 'Tennis Rating Converter: UTR, NTRP, WTN, ITN';
const PAGE_DESCRIPTION =
  'Convert your UTR, NTRP, Dynamic NTRP, WTN, ITN, or self-rated tennis rating instantly. See equivalents across every major system with confidence ranges.';
const PUBLISHED_DATE = '2026-05-14';

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const softwareApplicationJsonLd = generateSoftwareApplicationJsonLd({
    name: 'Tennis Rating Converter',
    description: PAGE_DESCRIPTION,
    url: PAGE_URL,
    applicationCategory: 'SportsApplication',
    operatingSystem: 'Web',
    publisherId: ORGANIZATION_ID,
    offers: [{ name: 'Free', monthlyUsd: 0 }],
  });

  const articleJsonLd = generateArticleJsonLd({
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    image: [`${SITE_URL}/icons/social-share.png`],
    datePublished: PUBLISHED_DATE,
    dateModified: PUBLISHED_DATE,
    publisher: {
      name: BRAND_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/icons/social-share.png`,
    },
    mainEntityOfPage: PAGE_URL,
    speakable: {
      cssSelector: ['.tool-tldr', '.ratings-table'],
    },
  });

  const faqJsonLd = generateFaqJsonLd({
    items: FAQS.map((faq) => ({
      question: faq.question,
      answer: faq.answer,
    })),
  });

  const breadcrumbJsonLd = generateBreadcrumbListJsonLd({
    items: [
      { id: SITE_URL, name: 'Home' },
      { id: PAGE_URL, name: 'Tennis Rating Converter' },
    ],
  });

  const howToJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to convert your tennis rating',
    description:
      'Three steps to see your tennis rating in every major rating system, with confidence ranges.',
    totalTime: 'PT15S',
    tool: [{ '@type': 'HowToTool', name: 'Tennis Rating Converter' }],
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Enter your rating',
        text: 'Type your current rating into the input field. The converter accepts decimal values matching each system’s scale.',
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Choose your rating system',
        text: 'Select which system your rating comes from: UTR, NTRP, Dynamic NTRP, WTN, ITN, or self-rated. For NTRP-family ratings, pick the division (men’s or women’s) for accurate UTR conversion.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'See your equivalents',
        text: 'The converter instantly shows equivalent ratings across all the other systems, with confidence ranges and plain-English descriptions.',
      },
    ],
  };

  const datasetJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'Tennis Rating System Comparison Table',
    description:
      'Cross-reference table mapping skill tiers across UTR, NTRP, Dynamic NTRP, WTN, ITN, and USTA self-rated tennis systems with confidence ranges.',
    url: `${PAGE_URL}#comparison-heading`,
    creator: { '@id': ORGANIZATION_ID },
    keywords: [
      'tennis rating',
      'UTR',
      'NTRP',
      'Dynamic NTRP',
      'WTN',
      'World Tennis Number',
      'ITN',
      'USTA self-rating',
    ],
    variableMeasured: PRIMARY_TENNIS_SYSTEM_IDS.map((id) => {
      const system = TENNIS_SYSTEMS[id];
      return `${system.shortName} (${system.scaleMin.toFixed(1)}–${system.scaleMax.toFixed(1)}${
        system.isInverted ? ', lower = stronger' : ''
      })`;
    }),
  };

  const definedTermSetJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    '@id': `${PAGE_URL}#rating-systems`,
    name: 'Tennis Rating Systems',
    description:
      'Glossary of tennis rating systems used by recreational, league, junior, college, and tournament players.',
    hasDefinedTerm: PRIMARY_TENNIS_SYSTEM_IDS.map((id) => {
      const system = TENNIS_SYSTEMS[id];
      return {
        '@type': 'DefinedTerm',
        name: system.shortName,
        alternateName: system.longName,
        description: system.oneLineDescription,
        url: system.websiteUrl,
        inDefinedTermSet: `${PAGE_URL}#rating-systems`,
      };
    }),
  };

  const combinedSchemas = combineJsonLdSchemas([
    softwareApplicationJsonLd,
    articleJsonLd,
    faqJsonLd,
    breadcrumbJsonLd,
    howToJsonLd,
    datasetJsonLd,
    definedTermSetJsonLd,
  ]);

  return {
    props: {
      jsonLd: JSON.stringify(combinedSchemas),
    },
  };
};

export default TennisRatingConverter;
